import { getNormalizedScrollLeft } from './getNormalizedScrollLeft';
import { getScrollLeftForNormalizedValue } from './getScrollLeftForNormalizedValue';

/**
 * Scrolls the specified item element just enough to ensure it is fully visible within the scroll container.
 *
 * This function calculates the normalized scrollLeft position for the container and checks if the item is out of view
 * on either side. If so, it adjusts the scroll position to bring the item fully into view, respecting RTL layouts.
 *
 * When scrolling is necessary, it optionally performs smooth animated scrolling via the native scrollTo API,
 * converting between normalized scrollLeft values and browser-specific scrollLeft using the helper utilities.
 *
 * @param scroller - The scroll container element.
 * @param item - The child element within the container to scroll into view.
 * @param isRTL - True if the container is in a right-to-left layout.
 * @param withAnimation - If true, scrolls with smooth animation; otherwise, jumps instantly.
 */
export function scrollItemToVisible(
    scroller: HTMLElement,
    item: HTMLElement,
    isRTL: boolean,
    withAnimation = false
): void {
    const scrollLeft = getNormalizedScrollLeft(scroller, isRTL);
    const viewRight = scrollLeft + scroller.clientWidth;
    const itemLeft = item.offsetLeft;
    const itemRight = itemLeft + item.offsetWidth;

    let target = scrollLeft;

    if (itemLeft < scrollLeft) {
        target = itemLeft;
    } else if (itemRight > viewRight) {
        target = itemRight - scroller.clientWidth;
    }

    if (target !== scrollLeft) {
        if (withAnimation) {
            scroller.scrollTo({
                left: getScrollLeftForNormalizedValue(scroller, target, isRTL),
                behavior: 'smooth',
            });
        } else {
            // eslint-disable-next-line no-param-reassign
            scroller.scrollLeft = getScrollLeftForNormalizedValue(scroller, target, isRTL);
        }
    }
}
